import { QuestionItem } from "../types/form";
import { visibleQuestionIds } from "./formVisibility";
import { questionInputName } from "./categoryValidation";
import { getValidationRules } from "./validationRules";

/**
 * Keeps a "how many" answer and the list it sizes in step (client worklist
 * #81–#83, the N-400 children section: q61 sizes q62).
 *
 * They were two sources of truth that never synced. Lowering the count left
 * every entry on the page (#83); removing an entry left the count where it
 * was, so Save & Next demanded the original number and the list was padded
 * back on the next load — Remove looked like it did nothing (#82).
 *
 * This runs ONLY on the applicant's own edits, never when saved answers are
 * loaded. Bugged saves already exist where the count is lower than the filled
 * entries (application 61 on dev: count 1, two children filled); reconciling
 * on load would silently delete a child's details.
 *
 * And it never deletes a FILLED entry on its own. Lowering the count drops
 * only empty trailing entries: typing "12" over "10" passes through "1" on the
 * way, and trimming filled entries there would delete children 2–10 between
 * two keystrokes. Filled extras stay; the entry-count message asks the
 * applicant to remove them, and Remove now works.
 */

type Pair = { sourceName: string; listName: string; source?: QuestionItem; list: QuestionItem };

function countDrivenPairs(questions: QuestionItem[]): Pair[] {
  const byId = new Map(questions.map((q) => [Number(q.id), q]));
  const pairs: Pair[] = [];
  for (const list of questions) {
    const sourceId = Number(getValidationRules(list)?.initializeEntryCountFrom?.questionId);
    if (!Number.isInteger(sourceId) || sourceId <= 0) continue;
    if ((list.answerType ?? list.type) !== "composite" || !list.allowMultipleAnswers) continue;
    const source = byId.get(sourceId);
    pairs.push({
      sourceName: source ? questionInputName(source) : `q_${sourceId}`,
      listName: questionInputName(list),
      source,
      list,
    });
  }
  return pairs;
}

function readCount(value: unknown): number | null {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

function toEntries(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) return value as Record<string, unknown>[];
  if (value && typeof value === "object") return [value as Record<string, unknown>];
  return [];
}

export function isEmptyEntry(entry: unknown): boolean {
  if (!entry || typeof entry !== "object") return true;
  return Object.values(entry as Record<string, unknown>).every(
    (v) => v === null || v === undefined || String(v).trim() === "",
  );
}

/** Same type the count was already stored as, so nothing else sees a change of type. */
function asCountLike(previous: unknown, n: number): number | string {
  return typeof previous === "string" ? String(n) : n;
}

/**
 * Given the form state before and after one edit, returns the state with the
 * count and its list reconciled. Returns `next` unchanged when the edit did
 * not touch a count-driven pair.
 */
export function reconcileCountDrivenEntries(
  prev: Record<string, any>,
  next: Record<string, any>,
  questions: QuestionItem[],
): Record<string, any> {
  let out = next;
  for (const { sourceName, listName } of countDrivenPairs(questions)) {
    const countBefore = readCount(prev[sourceName]);
    const countAfter = readCount(out[sourceName]);
    const listBefore = toEntries(prev[listName]);
    const listAfter = toEntries(out[listName]);

    if (countAfter !== null && countAfter !== countBefore) {
      // The applicant changed the count: size the list to it.
      const resized = listAfter.slice();
      while (resized.length < countAfter) resized.push({});
      while (resized.length > countAfter && isEmptyEntry(resized[resized.length - 1])) {
        resized.pop();
      }
      out = { ...out, [listName]: resized };
      continue;
    }

    if (
      countBefore !== null &&
      listAfter.length < listBefore.length &&
      listAfter.length < countBefore
    ) {
      // The applicant removed an entry and the list is now shorter than the
      // count: the count follows, or the removal would be undone.
      out = { ...out, [sourceName]: asCountLike(prev[sourceName], listAfter.length) };
    }
  }
  return out;
}

/**
 * Before saving: a count-driven pair that is hidden is sent as empty.
 *
 * Answering "No" to having children hides the count and the list, but their
 * old values were still sent and saved — and the PDF filler maps the list
 * straight onto the N-400, so children the applicant said they don't have
 * could be printed on the form. Clearing on SAVE rather than on the click
 * means an accidental "No" loses nothing until Save & Next.
 *
 * Only keys already in the payload are touched, so a save of another section
 * never clears these.
 */
export function clearHiddenCountDrivenAnswers(
  payload: Record<string, any>,
  formData: Record<string, any>,
  questions: QuestionItem[],
): Record<string, any> {
  let out = payload;
  // Transitive (client worklist #84): the list is hidden whenever its count
  // question is, whatever the count still holds.
  const visible = visibleQuestionIds(questions, formData);
  for (const { sourceName, listName, source, list } of countDrivenPairs(questions)) {
    const sourceHidden = source ? !visible.has(Number(source.id)) : false;
    const listHidden = !visible.has(Number(list.id));
    if (sourceHidden && sourceName in out) out = { ...out, [sourceName]: "" };
    if ((sourceHidden || listHidden) && listName in out) out = { ...out, [listName]: [] };
  }
  return out;
}
